var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { isApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { useAdvancedApprovalState } from 'app/domains/approval';
import { useCreateAssistantStream } from 'app/domains/assistant/hooks/useCreateAssistantStream';
import { useActiveDocument } from 'shared/domains/documents';
import { CreateApprovalCheckChatDocument } from './CreateApprovalCheckChat.gql';
import { SendApprovalCheckPromptDocument } from './SendApprovalCheckPrompt.gql';
import { SyncAssistantCreditsDocument } from './SyncAssistantCredits.gql';
import { zDocumentApprovalResultSchema } from './schemas';
import { ApprovalCheckErrorSubcodes } from './types';
export const useStreamApprovalCheck = () => {
    const { id: documentId } = useActiveDocument();
    const { response: initialResponse, setDocumentState } = useAdvancedApprovalState(documentId);
    const createEventSource = useCreateAssistantStream();
    const [createChatMutation] = useMutation(CreateApprovalCheckChatDocument);
    const [sendPromptMutation] = useMutation(SendApprovalCheckPromptDocument);
    const [syncCreditsQuery] = useLazyQuery(SyncAssistantCreditsDocument);
    const [state, setState] = useState(initialResponse ? 'done' : 'initial');
    const [error, setError] = useState(null);
    const [response, setResponse] = useState(initialResponse || {
        summary: '',
        isApprovalRequired: false,
        approvers: [],
    });
    const handleStreamResponse = (_a) => __awaiter(void 0, [_a], void 0, function* ({ chatId, streamToken }) {
        const evtSource = yield createEventSource(streamToken, chatId, 'document');
        const closeStream = () => {
            evtSource.close();
            setState('done');
        };
        evtSource.onmessage = (event) => {
            const [, [kind, payload]] = JSON.parse(event.data);
            if (kind === 'END') {
                closeStream();
                return;
            }
            const { success, data } = zDocumentApprovalResultSchema.safeParse(JSON.parse(payload));
            if (success) {
                setResponse(data);
                setDocumentState({ skipped: true, response: data });
            }
            else {
                setError('streaming');
                closeStream();
            }
        };
        // The server will close the connection after sending the end token, this is treated as
        // an error state by the EventSource, and we thus close the connection.
        evtSource.onerror = () => {
            closeStream();
        };
    });
    const resetApprovalCheck = () => {
        setError(null);
        setState('initial');
        setResponse({ summary: '', isApprovalRequired: false, approvers: [] });
    };
    const createChat = (_b) => __awaiter(void 0, [_b], void 0, function* ({ promptId, context }) {
        const { data } = yield createChatMutation({ variables: { documentId } });
        const chatId = data === null || data === void 0 ? void 0 : data.createDocumentChat.id;
        if (!chatId) {
            throw new Error('Failed to create chat');
        }
        return { chatId, promptId, context };
    });
    const handleError = (err) => {
        var _a, _b;
        setState('initial');
        if (isApolloError(err)) {
            const subcode = (_b = (_a = err.graphQLErrors[0]) === null || _a === void 0 ? void 0 : _a.extensions) === null || _b === void 0 ? void 0 : _b.subcode;
            if (subcode === ApprovalCheckErrorSubcodes.InsufficientCredits) {
                syncCreditsQuery();
                setError('credits');
            }
        }
        else {
            setError('setup');
        }
    };
    const sendPrompt = (_c) => __awaiter(void 0, [_c], void 0, function* ({ chatId, promptId, context }) {
        var _d;
        const { data } = yield sendPromptMutation({ variables: { chatId, promptId, context } });
        const streamToken = (_d = data === null || data === void 0 ? void 0 : data.lastChat.messages[0]) === null || _d === void 0 ? void 0 : _d.streamToken;
        if (!streamToken) {
            throw new Error('Failed to send prompt');
        }
        return { chatId, streamToken };
    });
    const startApprovalCheck = (promptId, context) => __awaiter(void 0, void 0, void 0, function* () {
        setState('evaluating');
        yield createChat({ promptId, context }).then(sendPrompt).then(handleStreamResponse).catch(handleError);
    });
    return { startApprovalCheck, resetApprovalCheck, state, response, error };
};
